import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { GridComponent } from '@syncfusion/ej2-angular-grids';
import { InventoryService } from '../../../services/inventory.service';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent } from 'src/app/Shared/shared-imports';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import { VendorSearchComponent } from 'src/app/Shared/vendor-search/vendor-search.component';
import { ProductService } from 'src/app/services/Product.service';
import { VendorService } from 'src/app/services/Vendor.service';
import { GeneralService } from 'src/app/services/general.service';
import { PosSettingsService } from 'src/app/services/pos-settings.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PercentageFormatterDirective } from 'src/app/Shared/percentageFormatter.directive';
import { CurrencyFormatterDirective } from 'src/app/Shared/currencyFormatter.directive';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { TooltipModule } from "@syncfusion/ej2-angular-popups";
import { EActionTypes, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { ReportsService } from 'src/app/services/report.service';
import { IReportResponse } from 'src/app/Shared/model/ReportsModel';
import { OperationRentalService } from 'src/app/services/Rentals.service';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';

@Component({
  selector: 'app-ops-new-inventory',
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, TopBarComponent, WordReplacePipe,
    VendorSearchComponent, PercentageFormatterDirective, CurrencyFormatterDirective,
    UserPermissionHandlerDirective, TooltipModule, LoaderComponent],
  standalone: true,
  templateUrl: './ops-new-inventory.html',
  styleUrl: './ops-new-inventory.scss'
})
export class OpsNewInventory implements OnInit, OnDestroy {
  isCollapsed = true;
  inventoryForm!: FormGroup;
  currentItemForm!: FormGroup;
  private destroy$ = new Subject<void>();

  itemsData: any[] = [];
  selectedItemIndex: number | null = null;
  currentImagePreview: string | null = null;
  barcodeTemplates: any[] = [];
  selectedBarcodeTemplate: any = null;
  defaultBarcodeTemplateId: number | null = null;
  defaultBarcodeTemplateName: string | null = null;
  productCategory: { CatSeqCategoryId: number, CatTxtCategoryDescr: string }[] = [];
  isLoadedInventory: boolean = false;
  searchFieldId: number = 0;
  searchValue: string = '';
  searchDate?: Date;
  stockInwardSummaries: any[] = [];
  searchValue$ = new Subject<string>();
  selectedStockInward: any = null;
  loaderIcon: Promise<boolean>;
  reportUrl: any;
  marketId: any;
  flatCommissionValue: number = 0;
  useBarcodeScanner: boolean = false;
  editedCountString: string = '';
  uniqueSessionId: string = '';
  autoGenerateStockId: boolean = false;
  public today: Date = new Date();

  @ViewChild('itemsGrid', { static: false }) public itemsGrid!: GridComponent;
  @ViewChild('stockInwardGrid') stockInwardGrid!: GridComponent;

  VendorFilterOption = {
    All: true,
    Current: true,
    Inactive: false,
    PageModel: {
      pageCount: 1,
      pageSize: 20
    }
  };

  pageNumber = 0;
  pageSize = 100;
  totalRecords = 0;
  loading = false;
  private scrollAttached = false;
  private boundScroll = this.onGridScroll.bind(this);

  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.NewInventory, Action: EActionTypes.VIEW };
  createPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.NewInventory, Action: EActionTypes.CREATE };

  constructor(
    private fb: FormBuilder,
    private inventoryService: InventoryService,
    private toastr: ToastrService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private productService: ProductService,
    private vendorService: VendorService,
    private generalService: GeneralService,
    private posSettingsService: PosSettingsService,
    private wordReplacePipe: WordReplacePipe,
    private reportService: ReportsService,
    private rentalService: OperationRentalService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.initCurrentItemForm();
    this.loadProductCategories();
    this.loadBarcodeTemplateSettings();
    this.setupSearchValueListener();
    this.loadPosSettings();
    this.getReportUrl();
    const tabId = sessionStorage.getItem('tabId');
    const sessionKey = `UniqueSessionId-${tabId}`;
    this.uniqueSessionId = localStorage.getItem(sessionKey);
    this.rentalService.getDefaultMarketId().pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res) {
        this.marketId = res;
      }
    });

    this.items.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshItemsData();
        this.updateTotals();
      });
  }

  get summaryForm(): FormGroup {
    return this.inventoryForm.get('Summary') as FormGroup;
  }

  loadPosSettings(): void {
    this.posSettingsService.getPosSettings().subscribe({
      next: (response) => {

        this.autoGenerateStockId =
          response?.OtherSettings?.AutomaticInventoryStockID ?? false;
      },
      error: (error) => {
        console.error('Error loading POS settings', error);
      }
    });
  }

  private generateAutoRandomInvID(): string {
    const now = new Date();
    const hour = now.getUTCHours().toString().padStart(2, '0');
    const minute = now.getUTCMinutes().toString().padStart(2, '0');
    const second = now.getUTCSeconds().toString().padStart(2, '0');
    const millisecond = now.getUTCMilliseconds().toString().padStart(3, '0');

    const random = Math.floor(Math.random() * 900) + 100;

    let stockId =
      `${hour}${minute}${second}${random}`;

    stockId = stockId.substring(0, 9);
    return stockId;
  }

  onStockIdBlur(): void {
    const stockId = this.currentItemForm.get('StockId')?.value?.trim();
    if (!stockId) return;

    const vendorId = this.inventoryForm.get('Summary.VendorId')?.value;
    if (!vendorId) {
      const fieldName = this.wordReplacePipe.transform('Vendor');
      this.toastr.warning($localize`Please select a ${fieldName} first.`, $localize`Validation`, this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    this.inventoryService.checkStockIdExists(stockId, vendorId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (!res?.Success) {
          const errorMessage = this.wordReplacePipe.transform(res?.Message);
          const fieldName = this.wordReplacePipe.transform('Vendor');
          this.toastr.warning(
            errorMessage || $localize`Stock ID already used by different ${fieldName}. Please enter different Stock ID`,
            $localize`Validation`,
            this.sharedHTMLSettings.ToasterConfig
          );
          this.currentItemForm.get('StockId')?.setErrors({ invalid: true });
          return;
        }

        if (res?.Id > 0) {
          this.loadInventoryItemDetails(stockId);
          this.currentItemForm.get('Rate')?.disable();
          this.currentItemForm.get('FlatCommissionRate')?.disable();
        } else {
          this.currentItemForm.get('Rate')?.enable();
          this.currentItemForm.get('FlatCommissionRate')?.enable();
        }
      },
      error: () => {
        this.toastr.error($localize`Error checking StockId`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
      }
    });
  }

  private loadInventoryItemDetails(stockId: string): void {
    this.inventoryService.getInventoryItemsByStockId(stockId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item: any) => {
        if (item) {
          this.currentItemForm.patchValue({
            CategoryId: item.CategoryId,
            ItemDescription: item.ItemDescription,
            Rate: item.Rate,
            Quantity: item.Quantity,
            VendorDiscount: item.VendorDiscount,
            FlatCommissionRate: item.FlatCommissionRate,
            PurchaseCost: item.PurchaseCost,
            ReorderPoint: item.ReorderPoint,
            NoTax: item.NoTax,
            InventoryImage: item.InventoryImage
          });
          this.currentItemForm.get('Rate')?.disable();
          this.currentItemForm.get('FlatCommissionRate')?.disable();
        }
      },
      error: () => {
        this.toastr.error($localize`Error loading item details`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
      }
    });
  }

  loadPOSSettingsAndCommission(): void {
    const vendorId = this.inventoryForm.get('Summary.VendorId')?.value;
    if (!vendorId) {
      return;
    }
    this.vendorService.GetMarketVendorSettings(vendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (vendorSettings: any) => {
          // Vendor-specific flat commission enabled
          if (vendorSettings?.ApplyFlatCommissionPos) {

            this.flatCommissionValue =
              Number(vendorSettings?.FlatCommissionPos) || 0;

            this.currentItemForm.patchValue({
              FlatCommissionRate: this.formatPercentage(this.flatCommissionValue)
            }, { emitEvent: false });

            return;
          }

          // Otherwise use POS Settings commission
          this.posSettingsService.getSalesTaxAndCommission()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res: any) => {

                this.flatCommissionValue =
                  Number(res?.Commission?.FlatCommission) || 0;

                this.currentItemForm.patchValue({
                  FlatCommissionRate: this.formatPercentage(this.flatCommissionValue)
                }, { emitEvent: false });
              },
              error: (err) => {
                console.error('Failed to load POS settings', err);
              }
            });
        },
        error: (err) => {
          console.error('Failed to load vendor settings', err);

          // Fallback to POS commission
          this.posSettingsService.getSalesTaxAndCommission()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (res: any) => {

                this.flatCommissionValue =
                  Number(res?.Commission?.FlatCommission) || 0;

                this.currentItemForm.patchValue({
                  FlatCommissionRate: this.formatPercentage(this.flatCommissionValue)
                }, { emitEvent: false });
              }
            });
        }
      });
  }

  onTemplateChange(templateId: number) {
    if (!templateId) return;

    const selectedTemplate =
      this.barcodeTemplates.find(x => x.BarcodeTemplateSettingId == templateId);

    if (!selectedTemplate) {
      this.toastr.warning('Invalid template selected', 'Warning');
    }
  }

  setupSearchValueListener() {
    this.searchValue$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.loadStockInwardSummaries();
      });
  }

  onSearchFieldChanged(): void {
    this.searchValue = '';
    this.searchDate = null;

    if (this.searchValue$) {
      this.searchValue$.next('');
    }
    this.loadStockInwardSummaries(true);
    this.selectedStockInward = null;
  }

  onSearchValueChange(value: string) {
    this.searchValue = value;
    this.searchValue$.next(value);
  }

  onSearchClick() {
    this.searchFieldId = 0;
    this.searchValue = '';
    this.searchDate = null;
    this.selectedStockInward = null;
    this.loadStockInwardSummaries(true);
    const modal = new (window as any).bootstrap.Modal(
      document.getElementById('stockInwardSearchModal')
    );
    modal.show();
  }

  onStockRowSelected(args: any) {
    const modalEl = document.getElementById('stockInwardSearchModal');
    const modal = (window as any).bootstrap.Modal.getInstance(modalEl);
    modal?.hide();

    const inwardNo = args?.data?.StockInward;

    if (this.selectedStockInward?.StockInward === inwardNo) {
      this.selectedStockInward = null;
    } else {
      this.selectedStockInward = args?.data || null;
    }
    this.loadSelectedInventory();
  }

  private initForm(): void {
    this.inventoryForm = this.fb.group({
      Summary: this.fb.group({
        InwardNo: [null],
        VendorId: [null, Validators.required],
        VendorName: ['', Validators.required],
        MarketVendorId: ['', Validators.required],
        StockReceivedDate: [new Date(), Validators.required],
        StockRefNo: ['', Validators.maxLength(20)],
        Notes: [''],
        TotalQuantity: [null],
        TotalProducts: [null],
        UsedLabels: [0],
        DisplayReceipt: [true],
        PrintReceipt: [false],
        Items: this.fb.array([])
      })
    });
  }

  private initCurrentItemForm(): void {
    this.currentItemForm = this.fb.group({
      CategoryId: [null, Validators.required],
      StockId: ['', [Validators.required, Validators.maxLength(15)]],
      ItemDescription: ['', [Validators.required, Validators.maxLength(100)]],
      Rate: [null, Validators.required],
      Quantity: [null, Validators.required],
      VendorDiscount: [null],
      FlatCommissionRate: [null],
      PurchaseCost: [null],
      ReorderPoint: [null],
      NoTax: [false],
      InventoryImage: [null],
      UploadedImageContent: [null],
      ImageName: [null]
    });
    this.currentImagePreview = null;

    this.currentItemForm.get('Rate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(rate => {
        const qtyControl = this.currentItemForm.get('Quantity');
        if (rate != null && rate !== '' && Number(rate) > 0) {
          if (!qtyControl?.value || Number(qtyControl.value) <= 0) {
            qtyControl?.setValue(1);
          }
        }
      });
  }

  private hasMoreRecords = true;

  loadStockInwardSummaries(reset = true): void {
    if (this.loading)
      return;
    if (!this.hasMoreRecords && !reset)
      return;

    this.loading = true;
    if (reset) {
      this.pageNumber = 0;
      this.hasMoreRecords = true;
      this.stockInwardSummaries = [];
      this.scrollAttached = false;
    }
    this.inventoryService
      .getInventoryStockInwardDetails(
        this.searchFieldId,
        this.searchValue,
        this.searchDate,
        this.pageNumber,
        this.pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.totalRecords = res.Count;
          this.stockInwardSummaries = [
            ...this.stockInwardSummaries,
            ...res.Result
          ];
          this.pageNumber += res.Result.length;
          this.hasMoreRecords = this.pageNumber < this.totalRecords;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.toastr.error(
            'Error loading Stock Inward summaries',
            'Error',
            this.sharedHTMLSettings.ToasterConfig);
        }
      });
  }

  onGridScroll(event: any) {
    if (this.loading)
      return;
    if (!this.hasMoreRecords)
      return;
    const element = event.target;
    const remaining = element.scrollHeight - element.scrollTop - element.clientHeight;

    if (remaining > 20)
      return;
    this.loadStockInwardSummaries(false);
  }

  onDateChanged(date: Date) {
    if (date instanceof Date) {
      this.searchDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      this.loadStockInwardSummaries();
    }
  }

  loadSelectedInventory(): void {
    if (this.selectedStockInward && this.selectedStockInward.StockInward) {
      const inwardNo = this.selectedStockInward.StockInward;

      this.loadInventory(inwardNo);
      this.selectedStockInward = null;
    }
  }

  onGridDataBound(): void {
    if (!this.stockInwardSummaries?.length) {
      this.selectedStockInward = null;
    }

    if (this.scrollAttached)
      return;

    const content = this.stockInwardGrid?.element?.querySelector('.e-content');

    if (content) {
      this.scrollAttached = true;
      content.addEventListener('scroll', this.boundScroll);
    }
  }

  onVendorSelected(vendor: any): void {
    if (!vendor) return;

    this.inventoryForm.patchValue({
      Summary: {
        VendorId: vendor.vendorId,
        VendorName: vendor.vendorName,
        MarketVendorId: vendor.marketVendorId
      }
    });

    this.loadPOSSettingsAndCommission();

    const modalEl = document.getElementById('vendorSearchModal');
    if (modalEl) {
      const modalInstance = window['bootstrap']?.Modal.getInstance(modalEl)
        || new window['bootstrap'].Modal(modalEl);
      modalInstance.hide();
    }

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }

  getVendorById(): void {
    const marketVendorId = this.inventoryForm.get('Summary.MarketVendorId')?.value;

    if (!marketVendorId) {
      this.inventoryForm.patchValue({
        Summary: {
          VendorId: null,
          VendorName: ''
        }
      }, { emitEvent: false });
      return;
    }

    this.vendorService.getVendorInfoByMarketVendorId(marketVendorId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (vendor: any) => {
          if (vendor.VendorId != 0) {
            this.inventoryForm.patchValue({
              Summary: {
                VendorId: vendor.VendorId,
                VendorName: vendor.VendorName
              }
            }, { emitEvent: false });

            this.loadPOSSettingsAndCommission();
          } else {
            Swal.fire({
              icon: 'warning',
              title: 'Warning',
              text: 'There are no Vendors with this ID.',
              confirmButtonText: 'OK'
            });
            this.inventoryForm.patchValue({
              Summary: {
                VendorId: null,
                VendorName: ''
              }
            }, { emitEvent: false });
          }
        },
        error: () => {
          const fieldName = this.wordReplacePipe.transform('Vendor');
          this.toastr.error(`Error loading ${fieldName}`, 'Error', this.sharedHTMLSettings.ToasterConfig);
        }
      });
  }

  loadProductCategories(): void {
    this.productService.GetAllProductCategories().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (Array.isArray(res)) {
          this.productCategory = res
            .filter((item: any) => item.CatBlnCategoryInactive === false || item.CatBlnCategoryInactive === null)
            .map(item => ({
              CatSeqCategoryId: item.CatSeqCategoryId,
              CatTxtCategoryDescr: item.CatTxtCategoryDescr
            }));
        } else {
          console.warn('Unexpected response:', res);
          this.productCategory = [];
        }
      },
      error: err => {
        this.toastr.error('Error loading categories', 'Error', this.sharedHTMLSettings.ToasterConfig);
        this.productCategory = [];
      }
    });
  }

  categoryValueAccessor = (field: string, data: any, column: any) => {
    const cat = this.productCategory.find(c => c.CatSeqCategoryId === data[field]);
    return cat ? cat.CatTxtCategoryDescr : '';
  };

  loadBarcodeTemplateSettings(): void {
    this.inventoryService.getBarcodeTemplateSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.barcodeTemplates = res;

          this.loadDefaultBarcodeTemplate();
        },
        error: (err) => {
          console.error('Error fetching barcode template settings', err);
        }
      });
  }

  loadDefaultBarcodeTemplate(): void {
    this.posSettingsService.getPosSettings()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (settings) => {
          const templateName = settings?.OtherSettings?.DefaultBarcodeTemplate?.toLowerCase()?.trim();

          const matchedTemplate = this.barcodeTemplates.find(x =>
            x.TemplateName?.toLowerCase()?.trim() === templateName
          );

          const selectedTemplate = matchedTemplate || this.barcodeTemplates[0];

          if (selectedTemplate) {
            this.defaultBarcodeTemplateId = selectedTemplate.BarcodeTemplateSettingId;
            this.selectedBarcodeTemplate = this.defaultBarcodeTemplateId;
          }

          this.useBarcodeScanner = settings?.OtherSettings?.UseBarcodeScanner === true;
        },
        error: (err) => console.error('Failed to load POS settings', err)
      });
  }

  private resetBarcodeTemplateToDefault(): void {
    if (this.defaultBarcodeTemplateId !== null) {
      this.selectedBarcodeTemplate = this.defaultBarcodeTemplateId;
    }
  }

  get items(): FormArray {
    return this.inventoryForm.get(['Summary', 'Items']) as FormArray;
  }

  addOrUpdateItem(): void {
    const stockIdControl = this.currentItemForm.get('StockId');
    let itemValue = this.currentItemForm.getRawValue();

    let stockId = itemValue.StockId?.toString().trim();

    if (this.autoGenerateStockId && !stockId) {
      stockId = this.generateAutoRandomInvID();
      stockIdControl?.setValue(stockId);
      itemValue = this.currentItemForm.getRawValue();
    }

    const item = {
      ...itemValue,
      FlatCommissionRate: itemValue.FlatCommissionRate
        ? parseFloat(itemValue.FlatCommissionRate.toString().replace('%', ''))
        : 0
    };

    if (!itemValue.CategoryId) {
      this.toastr.warning($localize`Please select a Category`, $localize`Validation`, this.sharedHTMLSettings.ToasterConfig);
      return;
    }
    if (!itemValue.StockId?.trim() && !this.autoGenerateStockId) {
      this.toastr.warning($localize`Please enter Stock ID`, $localize`Validation`, this.sharedHTMLSettings.ToasterConfig);
      return;
    }
    if (!itemValue.ItemDescription?.trim()) {
      this.toastr.warning($localize`Please enter Item Description`, $localize`Validation`, this.sharedHTMLSettings.ToasterConfig);
      return;
    }
    if (itemValue.Rate == null || itemValue.Rate === '' || Number(itemValue.Rate) <= 0) {
      this.toastr.warning($localize`Please enter a valid Rate`, $localize`Validation`, this.sharedHTMLSettings.ToasterConfig);
      return;
    }
    if (itemValue.Quantity == null || itemValue.Quantity === '' || Number(itemValue.Quantity) <= 0) {
      this.toastr.warning($localize`Please enter a valid Quantity`, $localize`Validation`, this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    const existsInGrid = this.items.controls.some((ctrl, idx) => {
      return idx !== this.selectedItemIndex && ctrl.get('StockId')?.value === itemValue.StockId;
    });
    if (existsInGrid) {
      this.toastr.warning(
        $localize`Stock ID: ${itemValue.StockId} has been already entered below..!`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const vendorId = this.inventoryForm.get('Summary.VendorId')?.value;

    this.inventoryService.checkStockIdExists(itemValue.StockId, vendorId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        if (!res?.Success) {
          // Belongs to a different vendor
          const fieldName = this.wordReplacePipe.transform('Vendor');
          const errorMessage = this.wordReplacePipe.transform(res?.Message);
          this.toastr.warning(
            errorMessage || $localize`Stock ID already used by different ${fieldName}. Please enter different Stock ID`,
            $localize`Validation`,
            this.sharedHTMLSettings.ToasterConfig
          );
          return;
        }

        // Belongs to same vendor (res.Id == 1)
        if (res?.Id > 0) {
          item.IsExistingStockIdForVendor = true;
          const fieldName = this.wordReplacePipe.transform('Vendor');
          Swal.fire({
            title: $localize`Stock ID already exists`,
            text: $localize`The entered Stock ID already exists for this ${fieldName}. Do you still want to continue?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: $localize`Yes, continue`,
            cancelButtonText: $localize`No`
          }).then(result => {
            if (result.isConfirmed) {
              itemValue.StockId = itemValue.StockId?.trim()
              this.validateStockIdLengthAndProceed(item);
            }
          });
        } else {
          item.IsExistingStockIdForVendor = false;
          itemValue.StockId = itemValue.StockId?.trim();
          this.validateStockIdLengthAndProceed(item);
        }
      },
      error: () => {
        this.toastr.error($localize`Error checking StockId`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
      }
    });
  }

  private commitItem(normalizedItem: any): void {
    if (this.selectedItemIndex === null) {
      this.items.push(this.fb.group({ ...normalizedItem }));
    } else {
      const group = this.items.at(this.selectedItemIndex) as FormGroup;
      if (!normalizedItem.UploadedImageContent && group.value.InventoryImage) {
        normalizedItem.InventoryImage = group.value.InventoryImage;
      }
      group.patchValue(normalizedItem);
      this.selectedItemIndex = null;
    }

    this.initCurrentItemForm();
    this.refreshItemsData();
    this.updateTotals();

    this.currentItemForm.patchValue({
      FlatCommissionRate: this.formatPercentage(this.flatCommissionValue)
    }, { emitEvent: false });
  }

  private validateStockIdLengthAndProceed(item: any): void {
    const stockId = item?.StockId?.trim() || '';

    if (stockId.length > 9) {
      Swal.fire({
        title: $localize`Warning`,
        text: $localize`Barcode scanner will not read the Stock Id having more than 9 characters. Do you want to proceed?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: $localize`Yes`,
        cancelButtonText: $localize`No`
      }).then(result => {
        if (result.isConfirmed) {
          this.handleAddItemAfterValidation(item);
        }
      });
    } else {
      this.handleAddItemAfterValidation(item);
    }
  }

  private handleAddItemAfterValidation(itemValue: any): void {
    const normalizedItem = {
      ...itemValue,
      IsExistingStockIdForVendor:
        itemValue.IsExistingStockIdForVendor ?? false,
      Rate: itemValue.Rate != null ? Number(itemValue.Rate) : 0,
      Quantity: itemValue.Quantity != null ? Number(itemValue.Quantity) : 0,
      VendorDiscount: itemValue.VendorDiscount != null ? Number(itemValue.VendorDiscount) : 0,
      FlatCommissionRate: itemValue.FlatCommissionRate != null ? Number(itemValue.FlatCommissionRate) : 0,
      PurchaseCost: itemValue.PurchaseCost != null ? Number(itemValue.PurchaseCost) : 0
    };

    if (normalizedItem.PurchaseCost > normalizedItem.Rate) {
      Swal.fire({
        title: $localize`Purchase cost is greater than Rate`,
        text: $localize`Are you sure you want to proceed?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: $localize`Yes, proceed`,
        cancelButtonText: $localize`Cancel`
      }).then(result => {
        if (result.isConfirmed) {
          this.commitItem(normalizedItem);
        }
      });
    } else {
      this.commitItem(normalizedItem);
    }
  }

  removeItem(index: number): void {
    if (index < 0 || index >= this.items.length) return;

    this.items.removeAt(index);

    this.itemsData = this.items.controls.map((g, i) => {
      const v = g.value;
      return {
        SlNo: i + 1,
        CategoryId: v.CategoryId,
        StockId: v.StockId,
        ItemDescription: v.ItemDescription,
        Rate: v.Rate != null && v.Rate !== '' ? Number(v.Rate) : 0,
        Quantity: v.Quantity != null && v.Quantity !== '' ? Number(v.Quantity) : 0,
        VendorDiscount: v.VendorDiscount != null && v.VendorDiscount !== '' ? Number(v.VendorDiscount) : 0,
        FlatCommissionRate: v.FlatCommissionRate != null && v.FlatCommissionRate !== '' ? Number(v.FlatCommissionRate) : 0,
        PurchaseCost: v.PurchaseCost != null && v.PurchaseCost !== '' ? Number(v.PurchaseCost) : 0,
        ReorderPoint: v.ReorderPoint != null && v.ReorderPoint !== '' ? Number(v.ReorderPoint) : 0,
        NoTax: !!v.NoTax,
        InventoryImage: v.InventoryImage
      };
    });

    if (this.itemsGrid) {
      this.itemsGrid.dataSource = this.itemsData;
      this.itemsGrid.clearSelection();
    }

    this.selectedItemIndex = null;
    this.initCurrentItemForm();
    this.updateTotals();

    this.currentItemForm.patchValue({
      FlatCommissionRate: this.formatPercentage(this.flatCommissionValue)
    }, { emitEvent: false });
  }

  deleteSelectedItem(): void {
    if (this.selectedItemIndex === null) return;
    this.removeItem(this.selectedItemIndex);
  }

  private refreshItemsData(): void {
    this.itemsData = this.items.controls.map((g, i) => {
      const v = g.value;
      return {
        SlNo: i + 1,
        CategoryId: v.CategoryId,
        StockId: v.StockId,
        ItemDescription: v.ItemDescription,
        Rate: v.Rate,
        Quantity: v.Quantity,
        VendorDiscount: v.VendorDiscount,
        FlatCommissionRate: v.FlatCommissionRate,
        PurchaseCost: v.PurchaseCost,
        ReorderPoint: v.ReorderPoint,
        NoTax: v.NoTax,
        InventoryImage: v.InventoryImage
      };
    });

    if (this.itemsGrid) {
      this.itemsGrid.refresh();
    }
  }

  private updateTotals(): void {
    const items = this.itemsData || [];
    const totalQty = items.reduce((s, it) => s + (Number(it.Quantity) || 0), 0);
    const totalProducts = items.length;

    this.inventoryForm.get('Summary.TotalQuantity')?.setValue(totalQty, { emitEvent: false });
    this.inventoryForm.get('Summary.TotalProducts')?.setValue(totalProducts, { emitEvent: false });
  }

  onItemRowSelected(args: any): void {
    const rowData = args?.data;
    if (!rowData) return;

    const index = (rowData.SlNo ?? 1) - 1;
    if (index < 0 || index >= this.items.length) return;

    this.selectedItemIndex = index;
    const group = this.items.at(index) as FormGroup;
    this.currentItemForm.patchValue(group.value);

    if (group.value.IsExistingStockIdForVendor) {
      this.currentItemForm.get('Rate')
        ?.disable({ emitEvent: false });
      this.currentItemForm.get('FlatCommissionRate')
        ?.disable({ emitEvent: false });
    }
    else {
      this.currentItemForm.get('Rate')
        ?.enable({ emitEvent: false });
      this.currentItemForm.get('FlatCommissionRate')
        ?.enable({ emitEvent: false });
    }

    if (group.value.InventoryImage) {
      this.currentImagePreview = group.value.InventoryImage;
    } else if (group.value.UploadedImageContent) {
      this.currentImagePreview = `data:image/*;base64,${group.value.UploadedImageContent}`;
    } else {
      this.currentImagePreview = null;
    }
  }

  onFileChange(event: any): void {
    const file: File = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.split(',')[1];
      this.currentItemForm.patchValue({
        UploadedImageContent: base64,
        ImageName: file.name,
        InventoryImage: null
      });
      this.currentImagePreview = dataUrl;
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }

  triggerAddImage(fileInputEl: HTMLInputElement): void {
    fileInputEl.click();
  }

  toggleImage(fileInputEl: HTMLInputElement): void {
    if (this.currentImagePreview) {
      this.removeImage();
    } else {
      this.triggerAddImage(fileInputEl);
    }
  }

  removeImage(): void {
    this.currentImagePreview = null;

    this.currentItemForm.patchValue({
      UploadedImageContent: null,
      ImageName: null,
      InventoryImage: null
    });

    const fileInputEl = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInputEl) fileInputEl.value = '';
  }

  saveInventory(printBarcode: boolean = false): void {
    if (this.isLoadedInventory) {
      this.toastr.warning(
        $localize`Loaded invoice cannot be saved`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    const summary = this.inventoryForm.get('Summary') as FormGroup;

    summary.markAllAsTouched();
    this.currentItemForm.markAllAsTouched();

    if (summary.invalid) {
      const fieldName = this.wordReplacePipe.transform('Vendor');
      this.toastr.warning(
        $localize`Please fill ${fieldName} ID, ${fieldName} Name, and required details`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    if (this.items.length === 0) {
      this.toastr.warning(
        $localize`Please enter the Invoice particulars and then save.`,
        $localize`Validation`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    if (this.selectedItemIndex !== null) {
      Swal.fire({
        icon: 'warning',
        title: 'Validation',
        text: 'Please Update the Inventory Item and then Save',
        confirmButtonText: 'OK'
      });
      return;
    }

    Swal.fire({
      title: $localize`Are you sure?`,
      text: $localize`Do you want to save this inventory?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, save it`,
      cancelButtonText: $localize`Cancel`
    }).then(result => {
      if (!result.isConfirmed) return;

      this.inventoryForm.patchValue({
        Summary: {
          InwardNo: 0
        }
      });

      this.loaderIcon = Promise.resolve(true);

      const selectedTemplate = this.barcodeTemplates.find(x => x.BarcodeTemplateSettingId == this.selectedBarcodeTemplate);
      const templateName = String(selectedTemplate?.TemplateName ?? '').toLowerCase();
      const isThermalTemplate = selectedTemplate?.IsThermalPrint === true;

      const payload = this.inventoryForm.value;
      this.inventoryService.saveInventory(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            if (res?.Success) {
              this.loaderIcon = Promise.resolve(false);
              if (res?.MarketInwardNo) {
                this.inventoryForm.get('Summary.InwardNo')?.setValue(res.MarketInwardNo);
              }
              const barcodeIds = (res?.IdList || []).join(',');

              const summaryValues = this.inventoryForm.get('Summary')?.value;

              if (printBarcode && isThermalTemplate && res?.Id) {
                this.inventoryService.getInventoryByInwardNumber(res.Id).pipe(takeUntil(this.destroy$))
                  .subscribe((inventoryRes: any) => {
                    const items = inventoryRes?.Data?.Summary?.Items || [];
                    const thermalBarcodePrintData = items.map((item: any) => `${item.BarcodeId}:${item.Quantity}`).join(',');
                    const thermalLabelName = templateName.toLowerCase().includes('standard') ? 'standard' : 'butterfly';
                    this.printThermalBarcodeLabels(thermalBarcodePrintData, thermalLabelName);
                  });
              }
              else if (printBarcode && barcodeIds) {
                this.onReportView(barcodeIds);
              }

              if (summaryValues?.DisplayReceipt) {
                this.openReceiptReport();
              }

              this.toastr.success($localize`Inventory saved successfully`, $localize`Success`, this.sharedHTMLSettings.ToasterConfig);

              this.resetAllForms();
            } else {
              this.loaderIcon = Promise.resolve(false);
              this.toastr.warning(res?.Message || $localize`Failed to save inventory`, $localize`Validation`, this.sharedHTMLSettings.ToasterConfig);
            }
          },
          error: () => {
            this.loaderIcon = Promise.resolve(false);
            this.toastr.error($localize`Error saving inventory`, $localize`Error`, this.sharedHTMLSettings.ToasterConfig);
          }
        });
    });
  }

  getReportUrl() {
    this.generalService.getReportUrl().subscribe((response: any) => {
      this.reportUrl = response;
    });
  }

  invokeReport(obj) {
    var openInNewTab = '_blank';
    this.loaderIcon = Promise.resolve(true);
    this.reportService.generateReport(obj).subscribe((response: IReportResponse) => {
      if (response.Success) {
        window.open(this.reportUrl + `report?id=` + response.ReportRequestId,
          openInNewTab, 'width=' + (screen.availWidth - 5) + ',height=' + (screen.availHeight - 5));
        this.loaderIcon = Promise.resolve(false);
      } else {
        console.log('Error on generating Operational Report');
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  onReportView(barcodeIds: string) {

    const summary = this.inventoryForm.get('Summary')?.value;

    const selectedTemplate = this.barcodeTemplates.find(
      x => x.BarcodeTemplateSettingId === this.selectedBarcodeTemplate
    );

    if (!selectedTemplate) {
      this.toastr.warning('Please select a barcode template', 'Warning', this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    const reportName = this.getReportName(selectedTemplate.TemplateName);

    const obj: any = {
      ReportName: reportName,
      ReportParameters: {},
      MarketId: this.marketId
    };

    obj.ReportParameters["MarketId"] = Number(this.marketId);
    obj.ReportParameters["VendorId"] = Number(summary.VendorId);
    obj.ReportParameters["BarcodeIds"] = barcodeIds;
    obj.ReportParameters["UsedLabel"] = Number(summary.UsedLabels || 0);

    obj.ReportParameters["MaxDisplayColumns"] = Number(selectedTemplate.MaxDisplayColumns || 3);
    obj.ReportParameters["DisplayWithLabels"] = selectedTemplate.DisplayWithLabels ? 1 : 0;
    obj.ReportParameters["DisplayVendorId"] = selectedTemplate.ShowVendorId ? 1 : 0;
    obj.ReportParameters["DisplayStockId"] = selectedTemplate.ShowStockId ? 1 : 0;
    obj.ReportParameters["DisplayItemName"] = selectedTemplate.ShowItemName ? 1 : 0;
    obj.ReportParameters["DisplayPrice"] = selectedTemplate.ShowPrice ? 1 : 0;

    obj.ReportParameters["IsConsignmentInventory"] = 0;
    obj.ReportParameters["BarcodeQtyOverrides"] = this.editedCountString;
    this.invokeReport(obj);
  }

  getReportName(templateName: string): string {
    const name = templateName?.toLowerCase()?.trim();

    if (name?.includes('5160'))
      return 'BarcodeAvery5160';

    if (name?.includes('5195'))
      return 'BarcodeAvery5195';

    if (name?.includes('6570'))
      return 'BarcodeAvery6570';

    return 'BarcodeAvery5160';
  }

  openReceiptReport() {
    const summary = this.inventoryForm.get('Summary')?.value;

    if (!summary?.InwardNo) {
      this.toastr.warning('Invalid Inward No', 'Warning', this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    const obj: any = {
      ReportName: "InventoryInwardReceipt",
      ReportParameters: {},
      MarketId: this.marketId
    };

    obj.ReportParameters["MarketId"] = Number(this.marketId);
    obj.ReportParameters["InwardNo"] = Number(summary.InwardNo);

    this.invokeReport(obj);
  }

  onPrintClick() {
    const summary = this.inventoryForm.get('Summary')?.value;

    if (!this.isLoadedInventory) return;

    if (summary?.DisplayReceipt) {
      this.openReceiptReport();
    } else {
      this.toastr.info('Please select Display Receipt option', 'Info', this.sharedHTMLSettings.ToasterConfig);
    }
  }

  allowOnlyDigits(event: Event, maxDigits: number, form: FormGroup): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.replace(/\D/g, '');
    if (maxDigits && value.length > maxDigits) {
      value = value.substring(0, maxDigits);
    }

    input.value = value;

    const controlName = input.getAttribute('formControlName');
    if (controlName) {
      form.get(controlName)?.setValue(value ? Number(value) : null, { emitEvent: false });
    }
  }

  loadInventory(inwardNo: number): void {
    if (!inwardNo || Number.isNaN(inwardNo)) {
      this.toastr.info(
        $localize`Please enter a valid Inward No to load`,
        $localize`Info`,
        this.sharedHTMLSettings.ToasterConfig
      );
      return;
    }

    while (this.items.length) this.items.removeAt(0);
    this.currentItemForm.reset();
    this.refreshItemsData();
    this.updateTotals();

    this.loaderIcon = Promise.resolve(true);
    this.inventoryService.getInventoryByInwardNumber(inwardNo)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          if (!res?.Success || !res?.Data) {
            this.isLoadedInventory = false;
            this.currentItemForm.enable({ emitEvent: false });

            this.inventoryForm.reset({
              Summary: {
                InwardNo: 0,
                VendorId: null,
                VendorName: '',
                MarketVendorId: '',
                StockReceivedDate: new Date(),
                StockRefNo: '',
                Notes: '',
                TotalQuantity: 0,
                TotalProducts: 0,
                UsedLabels: 0,
                DisplayReceipt: true,
                PrintReceipt: false,
                Items: []
              }
            });

            while (this.items.length) this.items.removeAt(0);
            this.refreshItemsData();
            this.updateTotals();

            this.loaderIcon = Promise.resolve(false);
            Swal.fire({
              icon: 'warning',
              title: 'Validation',
              text: 'This invoice does not exists!',
              confirmButtonText: 'OK'
            });
            return;
          }

          const incoming = { ...res.Data.Summary };
          if (incoming.StockReceivedDate) {
            incoming.StockReceivedDate = new Date(incoming.StockReceivedDate);
          }

          this.inventoryForm.patchValue({
            Summary: { ...incoming, Items: [] }
          });

          (incoming.Items || []).forEach((it: any) => {
            this.items.push(this.fb.group({
              BarcodeId: [it.BarcodeId ?? null],
              CategoryId: [it.CategoryId ?? null],
              StockId: [it.StockId ?? ''],
              ItemDescription: [it.ItemDescription ?? ''],
              Rate: [it.Rate ?? null],
              Quantity: [it.Quantity ?? null],
              VendorDiscount: [it.VendorDiscount ?? 0],
              FlatCommissionRate: [it.FlatCommissionRate ?? null],
              PurchaseCost: [it.PurchaseCost ?? null],
              ReorderPoint: [it.ReorderPoint ?? null],
              NoTax: [it.NoTax ?? false],
              InventoryImage: [it.InventoryImage ?? null]
            }));
          });

          this.refreshItemsData();
          this.updateTotals();

          this.isLoadedInventory = true;
          this.currentItemForm.disable({ emitEvent: false });
          this.loaderIcon = Promise.resolve(false);
        },
        error: () => {
          this.isLoadedInventory = false;
          this.currentItemForm.enable({ emitEvent: false });
          this.loaderIcon = Promise.resolve(false);
          this.toastr.error(
            $localize`Error loading inventory`,
            $localize`Error`,
            this.sharedHTMLSettings.ToasterConfig
          );
        }
      });
  }

  formatPercentage(value: number | string | null): string {
    if (value === null || value === undefined || value === '') return '0.00%';

    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `${num.toFixed(2)}%`;
  }

  newInventory(): void {
    this.isLoadedInventory = false;
    this.initForm();
    this.initCurrentItemForm();
    this.refreshItemsData();
    this.updateTotals();
    this.currentItemForm.enable({ emitEvent: false });
    this.resetBarcodeTemplateToDefault();
  }

  cancel() {
    const hasItems = this.itemsData && this.itemsData.length > 0;

    if (!this.isLoadedInventory && hasItems) {
      Swal.fire({
        title: 'Unsaved Changes',
        text: 'There are unsaved changes. Are you sure you want to cancel?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes',
        cancelButtonText: 'No'
      }).then((result) => {
        if (result.isConfirmed) {
          this.resetAllForms();
        }
      });
    } else {
      this.resetAllForms();
    }
  }

  resetAllForms() {
    this.initForm();
    this.initCurrentItemForm();
    this.refreshItemsData();
    this.updateTotals();
    this.currentItemForm.enable({ emitEvent: false });
    this.resetBarcodeTemplateToDefault();
    this.isLoadedInventory = false;
  }

  clearCurrentItem(): void {
    this.currentItemForm.reset();
    this.currentItemForm.enable();
    this.currentItemForm.patchValue({
      FlatCommissionRate: this.formatPercentage(this.flatCommissionValue)
    }, { emitEvent: false });

    this.currentImagePreview = null;
    this.selectedItemIndex = null;
  }

  reprintBarcode(): void {
    if (!this.isLoadedInventory) return;

    const summary = this.inventoryForm.get('Summary')?.value;

    if (!summary?.InwardNo) {
      this.toastr.warning('Invalid Inward No', 'Warning', this.sharedHTMLSettings.ToasterConfig);
      return
    }

    const selectedTemplate = this.barcodeTemplates.find(x => x.BarcodeTemplateSettingId === this.selectedBarcodeTemplate);
    const templateName = String(selectedTemplate?.TemplateName ?? '').toLowerCase();
    const isThermalTemplate = templateName.includes('standard') || templateName.includes('butterfly');

    const barcodeIds = this.items.controls
      .map((ctrl: any) => ctrl.get('BarcodeId')?.value)
      .filter((id: any) => !!id)
      .join(',');

    const thermalBarcodePrintData = this.items.controls
      .map((ctrl: any) => {
        const barcodeId = ctrl.get('BarcodeId')?.value;
        const qty = ctrl.get('Quantity')?.value;

        return barcodeId ? `${barcodeId}:${qty}` : null;
      })
      .filter((x: any) => !!x)
      .join(',');

    this.editedCountString = this.items.controls
      .map((ctrl: any) => {
        const barcodeId = ctrl.get('BarcodeId')?.value;
        const qty = ctrl.get('Quantity')?.value;

        return barcodeId ? `${barcodeId}:${qty}` : null;
      })
      .filter((id: any) => !!id)
      .join(',');

    if (!barcodeIds) {
      this.toastr.warning('No barcodes found to print', 'Warning', this.sharedHTMLSettings.ToasterConfig);
      return;
    }

    if (isThermalTemplate) {
      const thermalLabelName = templateName.toLowerCase().includes('standard') ? 'standard' : 'butterfly';
      this.printThermalBarcodeLabels(thermalBarcodePrintData, thermalLabelName);
    } else {
      this.onReportView(barcodeIds);
    }

  }

  printThermalBarcodeLabels(barcodePrintItems: string, labelType: string) {
    console.log(barcodePrintItems)
    const customProtocolUrl =
      'silentprinter://?methodname=inventorybarcodelabel' +
      '&param1=' + encodeURIComponent(barcodePrintItems) +
      '&param2=' + encodeURIComponent(labelType) +
      '&uniqueSessionId=' + this.uniqueSessionId;

    //console.log("Printing URL:", customProtocolUrl);

    window.location.href = customProtocolUrl;
  }

  onBeforeOpen(args: any): void {
    if (!args?.target?.dataset?.content) args.cancel = true;
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  ngOnDestroy(): void {
    const content = this.stockInwardGrid?.element?.querySelector('.e-content');

    if (content) {
      content.removeEventListener('scroll', this.boundScroll);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }
}
